import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../../../common/state/store";

export type GraphIssuesSummary = {
  errorCount: number;
  warningCount: number;
  unlinkedCount: number;
};

interface IGraphuState {
  isValid: boolean;
  isLoading: boolean;
  shouldRerender: boolean;
  showAllAssets: boolean;
  forceFullReload: boolean;
  isSilentRefetching: boolean;
  suppressSilentSpinner: boolean;
  issuesSummary: GraphIssuesSummary | null;
  issuesPanelOpen: boolean;
}
const initialState: IGraphuState = {
  isValid: false,
  isLoading: false,
  shouldRerender: false,
  showAllAssets: true,
  forceFullReload: false,
  isSilentRefetching: false,
  suppressSilentSpinner: false,
  issuesSummary: null,
  issuesPanelOpen: false,
};

export const graphuSlice = createSlice({
  name: "graphu",
  initialState,
  reducers: {
    setIsValid: (state, action: PayloadAction<boolean>) => {
      state.isValid = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsSilentRefetching: (state, action: PayloadAction<boolean>) => {
      state.isSilentRefetching = action.payload;
    },
    setSuppressSilentSpinner: (state, action: PayloadAction<boolean>) => {
      state.suppressSilentSpinner = action.payload;
    },
    setShouldRerender: (state) => {
      state.shouldRerender = !state.shouldRerender;
    },
    requestGraphFullReload: (state) => {
      state.forceFullReload = true;
    },
    clearGraphForceReload: (state) => {
      state.forceFullReload = false;
    },
    setShowAllAssets: (state, action: PayloadAction<boolean>) => {
      state.showAllAssets = action.payload;
    },
    toggleShowAllAssets: (state) => {
      state.showAllAssets = !state.showAllAssets;
    },
    setGraphIssuesSummary: (state, action: PayloadAction<GraphIssuesSummary | null>) => {
      state.issuesSummary = action.payload;
    },
    setIssuesPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.issuesPanelOpen = action.payload;
    },
  },
});

export const {
  setIsValid,
  setIsLoading,
  setIsSilentRefetching,
  setSuppressSilentSpinner,
  setShouldRerender,
  requestGraphFullReload,
  clearGraphForceReload,
  setGraphIssuesSummary,
  setIssuesPanelOpen,
} = graphuSlice.actions;

export const getIsLoading = (state: RootState) => state.graphu.isLoading;
export const getIsSilentRefetching = (state: RootState) => state.graphu.isSilentRefetching;
export const getSuppressSilentSpinner = (state: RootState) => state.graphu.suppressSilentSpinner;
export const getShouldRerenderGraph = (state: RootState) => state.graphu.shouldRerender;
export const getForceFullReload = (state: RootState) => state.graphu.forceFullReload;
export const getShowAllAssets = (state: RootState) => state.graphu.showAllAssets;
export const getGraphIssuesSummary = (state: RootState) => state.graphu.issuesSummary;
export const getIssuesPanelOpen = (state: RootState) => state.graphu.issuesPanelOpen;

export default graphuSlice.reducer;
